import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  getChartTheme,
  setChartTheme,
  onChartThemeChange,
  SELECTABLE_CHART_THEMES,
  CHART_THEMES,
  type ChartThemeName,
  type SelectableChartTheme,
} from './chartUtils';
import { t } from '@vitesse/base-ui/i18n';
import { LocaleController } from '@vitesse/base-ui/i18n';
import { iconButton } from '@vitesse/base-ui/elements/shared';
import '@vitesse/base-ui/elements/vitesse-icon';
import '@vitesse/base-ui/elements/vitesse-dialog';

// Localized label per theme (resolved at render so it follows the language).
const THEME_LABEL_KEYS: Record<SelectableChartTheme, `chartTheme.${SelectableChartTheme}`> = {
  frost: 'chartTheme.frost',
  peach: 'chartTheme.peach',
  citrus: 'chartTheme.citrus',
  candy: 'chartTheme.candy',
  mint: 'chartTheme.mint',
  berry: 'chartTheme.berry',
};

/*
 * <vitesse-chart-theme-picker> — the chart-column settings button: a gear that
 * opens the shared <vitesse-dialog> (TOP-LAYER, so it's never clipped by the grid
 * header's overflow) holding a theme selector. Selecting a theme applies it LIVE
 * (chartUtils.setChartTheme → the grid's render loop repaints next frame), mirror-
 * ing the agent's typewriter theme selector. Mounted in the grid chart header.
 *
 * All six themes render on ONE line; names are localised via chartTheme.* keys.
 */
@customElement('vitesse-chart-theme-picker')
export class ChartThemePicker extends LitElement {
  @state() private theme: ChartThemeName = getChartTheme();
  @state() private open = false;
  private unsub?: () => void;
  // The theme active when the dialog opened — restored if the user cancels.
  private originalTheme: ChartThemeName = getChartTheme();

  constructor() {
    super();
    new LocaleController(this); // re-label chrome on language change
  }
  connectedCallback(): void {
    super.connectedCallback();
    this.unsub = onChartThemeChange((th) => (this.theme = th)); // keep selection in sync
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsub?.();
  }

  // Selecting a theme applies it LIVE (preview); the dialog remembers the original
  // so X / Escape / backdrop revert, while Done keeps the new one.
  private openDialog(): void {
    this.originalTheme = getChartTheme();
    this.open = true;
  }
  private cancel(): void {
    setChartTheme(this.originalTheme); // revert the live preview
    this.open = false;
  }
  private commit(): void {
    this.open = false; // keep the previewed theme
  }

  static styles = [
    iconButton,
    css`
      :host {
        display: inline-flex;
        margin-left: auto; /* sit at the right edge of the chart column header */
      }
      .gear {
        color: var(--text-secondary); /* size comes from the .md size token */
      }
      .field-label {
        display: block;
        margin: 0 0 10px;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        color: var(--text-secondary);
      }
      .themes {
        display: flex;
        flex-wrap: nowrap; /* all 6 themes on ONE line */
        gap: 2px;
      }
      .theme {
        position: relative;
        flex: 1 1 0;
        min-width: 0;
        padding: 8px 2px 12px;
        border: none;
        background: none;
        font: inherit;
        font-size: 12px;
        text-align: center;
        white-space: nowrap;
        color: var(--text-secondary);
        cursor: pointer;
        transition: color 0.2s ease;
      }
      .theme:hover {
        color: var(--text-primary);
      }
      .theme.sel {
        color: var(--text-primary);
        font-weight: 600;
      }
      .bar {
        position: absolute;
        left: 12%;
        right: 12%;
        bottom: 4px;
        height: 2px;
        border-radius: 1px;
        opacity: 0;
        transform: scaleX(0.4);
        transition:
          opacity 0.2s ease,
          transform 0.2s ease;
      }
      .theme.sel .bar {
        opacity: 1;
        transform: scaleX(1);
      }
    `,
  ];

  render() {
    return html`
      <button class="icon-btn md gear" title=${t('grid.chartSettings')} @click=${() => this.openDialog()}>
        <vitesse-icon name="settings" size="16"></vitesse-icon>
      </button>
      <vitesse-dialog
        .open=${this.open}
        heading=${t('grid.chartSettings')}
        closeLabel=${t('common.close')}
        @dialog-close=${() => this.cancel()}
      >
        <span class="field-label">${t('chart.theme')}</span>
        <div class="themes">
          ${SELECTABLE_CHART_THEMES.map((name) => {
            const g = CHART_THEMES[name].gradient;
            return html`<button
              class="theme ${name === this.theme ? 'sel' : ''}"
              @click=${() => setChartTheme(name)}
            >
              ${t(THEME_LABEL_KEYS[name])}
              <span class="bar" style="background:linear-gradient(to right, ${g[0]}, ${g[1]})"></span>
            </button>`;
          })}
        </div>
        <vitesse-button slot="footer" color="blue" filled @click=${() => this.commit()}
          >${t('chart.done')}</vitesse-button
        >
      </vitesse-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-chart-theme-picker': ChartThemePicker;
  }
}
